import {
  Button,
  History,
  Input,
  PhoneInput,
  Plus,
  Search,
  Users,
  formatPhoneNumberForDisplay,
} from "@ews-admin/global-design-system";
import Papa from "papaparse";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { ActionMenu } from "../../components/molecules/ActionMenu/ActionMenu";
import { ConfirmationModal } from "../../components/molecules/ConfirmationModal/ConfirmationModal";
import { Modal } from "../../components/molecules/Modal/Modal";
import doctorInvitationApi from "../../services/doctorInvitation.api";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  bulkInviteDoctorsRequest,
  cancelInvitationRequest,
  clearInvitationError,
  fetchInvitationsRequest,
  inviteDoctorRequest,
  resendInvitationRequest,
  selectBulkInviteLoading,
  selectCancelLoading,
  selectInvitations,
  selectInvitationsError,
  selectInviteLoading,
  selectInvitationsLoading,
  selectResendLoading,
} from "../../store/reducers/doctorInvitationSlice";
import {
  BulkDoctorInvitationRequest,
  BulkRowError,
  DEFAULT_TRIAL_DAYS,
  DoctorInvitationEventDto,
  DoctorInvitationRequest,
  InvitationStatus,
  MAX_TRIAL_DAYS,
  NotificationMethod,
} from "../../types/doctorInvitation.definition";
import { formatDateTime, formatTimestamp } from "../../utils/date-format-utils";
import { notificationMethodLabel } from "../../utils/enum-label-utils";
import { isPhoneNumberValidForCountry } from "../../utils/phone-utils";

const STATUS_BADGE_CLASS: Record<InvitationStatus, string> = {
  ACCEPTED:
    "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800",
  PENDING:
    "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800",
  EXPIRED:
    "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800",
};

const CLICK_STATUS_BADGE_CLASS = {
  clicked:
    "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800",
  notClicked:
    "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-600",
};

const NOTIFICATION_METHODS: NotificationMethod[] = ["EMAIL", "SMS", "BOTH"];

type JourneyEntry = {
  type: "INVITED" | "CLICKED" | "RESENT" | "ACCEPTED" | "EXPIRED";
  timestamp: string;
  outcome?: string;
};

/**
 * MED-1519 / MED-1535: the journey is stitched together from two endpoints that
 * do NOT share a time base.
 *
 * `GET /doctors/invitations/{id}/events` types `createdAt` as an Instant, so it
 * carries a `Z` and is unambiguous. `GET /doctors/invitations` types
 * `invitedAt`, `acceptedAt` and `expirationDate` as a Java LocalDateTime: they
 * arrive with NO offset, and what they hold is the wall clock of whatever zone
 * the API's JVM runs in — `LocalDateTime.now()`, not UTC.
 *
 * So there is nothing to anchor them to here. MED-1519 first "fixed" this by
 * appending a `Z`, which assumed that zone was UTC; it is UTC on the servers
 * (Ansible sets the hosts to Africa/Dakar, UTC+0 year-round) but not on a
 * developer machine, where the invitation then displayed hours early — the
 * MED-1535 regression. Guessing is what caused the bug, so these values are
 * handed to the formatter as they are, which reads them as the viewer's local
 * time.
 *
 * That is correct only while the viewer's zone matches the API's. It is right
 * in Senegal and wrong by an hour in Tchad, exactly as it was before MED-1519.
 * MED-1534 removes the assumption for good by carrying the three fields as real
 * instants end to end; until then this code does not pretend to know better.
 *
 * `firstClickedAt` needs no handling either: the API has always sent it as an
 * Instant, and since MED-1519 the BFF declares it as one, so its `Z` survives.
 */
function buildJourneyEntries(
  invitation: {
    invitedAt: string;
    acceptedAt?: string;
    expirationDate: string;
    status: InvitationStatus;
  },
  events: DoctorInvitationEventDto[]
): JourneyEntry[] {
  const entries: JourneyEntry[] = [
    { type: "INVITED", timestamp: invitation.invitedAt },
  ];

  // Both event types come from the same endpoint, so both carry an offset and
  // can be compared directly.
  const timelineEvents = events
    .filter(
      e => e.eventType === "LINK_CLICKED" || e.eventType === "INVITATION_RESENT"
    )
    .slice()
    .sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  timelineEvents.forEach(e => {
    entries.push({
      type: e.eventType === "INVITATION_RESENT" ? "RESENT" : "CLICKED",
      timestamp: e.createdAt,
      outcome: e.outcome,
    });
  });

  if (invitation.status === "ACCEPTED" && invitation.acceptedAt) {
    entries.push({ type: "ACCEPTED", timestamp: invitation.acceptedAt });
  } else if (invitation.status === "EXPIRED") {
    entries.push({ type: "EXPIRED", timestamp: invitation.expirationDate });
  }

  return entries;
}

type InviteFormState = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  notificationMethod: NotificationMethod;
  /** MED-1695: kept as typed so a half-typed value does not snap back to a number. */
  trialDays: string;
};

const EMPTY_FORM: InviteFormState = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  notificationMethod: "EMAIL",
  trialDays: String(DEFAULT_TRIAL_DAYS),
};

/**
 * MED-1695: a whole number of days between 0 and MAX_TRIAL_DAYS, or null when the text is
 * not one. Mirrors the API's INVITATION_TRIAL_DAYS_INVALID check so the admin is told
 * before the request leaves.
 */
function parseTrialDays(raw: string): number | null {
  const text = raw.trim();
  if (!/^\d+$/.test(text)) return null;
  const value = Number(text);
  return value <= MAX_TRIAL_DAYS ? value : null;
}

// The CSV carries no trial length: one value, set in the modal, applies to the batch.
type ParsedRow = Omit<InviteFormState, "trialDays"> & { _rowError?: string };

const CSV_HEADERS = [
  "firstName",
  "lastName",
  "email",
  "phoneNumber",
  "notificationMethod",
];

function downloadTemplate() {
  const ws = XLSX.utils.aoa_to_sheet([
    CSV_HEADERS,
    ["Jean", "Dupont", "jean.dupont@example.com", "+221770000001", "EMAIL"],
    ["Fatou", "Diallo", "", "+221770000002", "SMS"],
  ]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Invitations");
  XLSX.writeFile(wb, "doctor_invitations_template.xlsx");
}

function normalizeMethod(raw: string): NotificationMethod {
  const upper = (raw ?? "").toUpperCase().trim();
  if (upper === "SMS") return "SMS";
  if (upper === "BOTH") return "BOTH";
  return "EMAIL";
}

function parseRawRows(rows: Record<string, string>[]): ParsedRow[] {
  return rows
    .filter(r => Object.values(r).some(v => v?.trim()))
    .map(r => ({
      firstName: (
        r["firstName"] ??
        r["first_name"] ??
        r["First Name"] ??
        ""
      ).trim(),
      lastName: (
        r["lastName"] ??
        r["last_name"] ??
        r["Last Name"] ??
        ""
      ).trim(),
      email: (r["email"] ?? r["Email"] ?? "").trim(),
      phoneNumber: (
        r["phoneNumber"] ??
        r["phone_number"] ??
        r["Phone Number"] ??
        ""
      ).trim(),
      notificationMethod: normalizeMethod(
        r["notificationMethod"] ??
          r["notification_method"] ??
          r["Notification Method"] ??
          "EMAIL"
      ),
    }));
}

/**
 * MED-1473: the one definition of "this row cannot be sent". The preview highlighted
 * such rows in red but the submit button stayed enabled, so the admin could fire an
 * import the API was bound to reject — the bulk facility import already blocks this.
 */
function rowHasErrors(row: ParsedRow): boolean {
  const missingName = !row.firstName || !row.lastName;
  const missingContact =
    ((row.notificationMethod === "EMAIL" ||
      row.notificationMethod === "BOTH") &&
      !row.email) ||
    ((row.notificationMethod === "SMS" || row.notificationMethod === "BOTH") &&
      !row.phoneNumber);
  return missingName || missingContact;
}

export const DoctorInvitationsManagement: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const invitations = useAppSelector(selectInvitations);
  const loading = useAppSelector(selectInvitationsLoading);
  const inviteLoading = useAppSelector(selectInviteLoading);
  const resendLoading = useAppSelector(selectResendLoading);
  const cancelLoading = useAppSelector(selectCancelLoading);
  const bulkInviteLoading = useAppSelector(selectBulkInviteLoading);
  const invitationsError = useAppSelector(selectInvitationsError);

  const [searchTerm, setSearchTerm] = useState("");
  // MED-1543: this was a boolean — "accepted" or "everything else" — which
  // gave an expired invitation no home of its own, so the "En attente" tab
  // listed invitations nobody is waiting on any more. The three statuses the
  // API can return each get a tab.
  const [statusTab, setStatusTab] = useState<InvitationStatus>("PENDING");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [form, setForm] = useState<InviteFormState>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<Partial<InviteFormState>>({});
  const [resendingId, setResendingId] = useState<number | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelTargetId, setCancelTargetId] = useState<number | null>(null);

  const [showBulkModal, setShowBulkModal] = useState(false);
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [bulkTrialDays, setBulkTrialDays] = useState(
    String(DEFAULT_TRIAL_DAYS)
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [journeyInvitationId, setJourneyInvitationId] = useState<number | null>(
    null
  );
  const [journeyEvents, setJourneyEvents] = useState<
    DoctorInvitationEventDto[]
  >([]);
  const [journeyLoading, setJourneyLoading] = useState(false);
  const [journeyError, setJourneyError] = useState(false);

  const prevInviteLoadingRef = useRef(false);
  const prevResendLoadingRef = useRef(false);
  const prevCancelLoadingRef = useRef(false);
  const prevBulkLoadingRef = useRef(false);

  useEffect(() => {
    dispatch(fetchInvitationsRequest());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearInvitationError());
    };
  }, [dispatch]);

  useEffect(() => {
    if (prevInviteLoadingRef.current && !inviteLoading) {
      if (invitationsError) {
        // DOCTOR_INVITATION_EMAIL_ALREADY_EXISTS and DOCTOR_INVITATION_PHONE_ALREADY_EXISTS
        // are surfaced via the GlobalErrorModal (apiErrors translations). No toast needed.
        const handledByGlobalModal =
          invitationsError.code === "DOCTOR_INVITATION_EMAIL_ALREADY_EXISTS" ||
          invitationsError.code === "DOCTOR_INVITATION_PHONE_ALREADY_EXISTS";
        if (!handledByGlobalModal) {
          toast.error(t("admin.doctorInvitations.toast.inviteError"));
        }
      } else {
        toast.success(t("admin.doctorInvitations.toast.inviteSuccess"));
        setShowInviteModal(false);
        setForm(EMPTY_FORM);
        setFormErrors({});
      }
    }
    prevInviteLoadingRef.current = inviteLoading;
  }, [inviteLoading, invitationsError, t]);

  useEffect(() => {
    if (prevResendLoadingRef.current && !resendLoading) {
      if (invitationsError) {
        toast.error(t("admin.doctorInvitations.toast.resendError"));
      } else if (resendingId !== null) {
        toast.success(t("admin.doctorInvitations.toast.resendSuccess"));
      }
      setResendingId(null);
    }
    prevResendLoadingRef.current = resendLoading;
  }, [resendLoading, invitationsError, resendingId, t]);

  useEffect(() => {
    if (prevCancelLoadingRef.current && !cancelLoading) {
      if (invitationsError) {
        toast.error(t("admin.doctorInvitations.toast.cancelError"));
      } else if (cancellingId !== null) {
        toast.success(t("admin.doctorInvitations.toast.cancelSuccess"));
      }
      setCancellingId(null);
    }
    prevCancelLoadingRef.current = cancelLoading;
  }, [cancelLoading, invitationsError, cancellingId, t]);

  useEffect(() => {
    if (prevBulkLoadingRef.current && !bulkInviteLoading) {
      if (invitationsError) {
        if (
          invitationsError.code !== "DOCTOR_INVITATION_BULK_VALIDATION_FAILED"
        ) {
          toast.error(t("admin.doctorInvitations.toast.bulkInviteError"));
        }
        // DOCTOR_INVITATION_BULK_VALIDATION_FAILED is displayed inline in the modal
      } else {
        toast.success(t("admin.doctorInvitations.toast.bulkInviteSuccess"));
        setShowBulkModal(false);
        setParsedRows([]);
        setParseError(null);
      }
    }
    prevBulkLoadingRef.current = bulkInviteLoading;
  }, [bulkInviteLoading, invitationsError, t]);

  useEffect(() => {
    if (journeyInvitationId === null) return;
    let cancelled = false;
    setJourneyLoading(true);
    setJourneyError(false);
    doctorInvitationApi
      .getInvitationEvents(journeyInvitationId)
      .then(events => {
        if (!cancelled) setJourneyEvents(events);
      })
      .catch(() => {
        if (!cancelled) setJourneyError(true);
      })
      .finally(() => {
        if (!cancelled) setJourneyLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [journeyInvitationId]);

  const countOf = (status: InvitationStatus) =>
    invitations.filter(i => i.status === status).length;

  const filteredInvitations = invitations.filter(inv => {
    if (inv.status !== statusTab) return false;
    const fullName = `${inv.firstName} ${inv.lastName}`.toLowerCase();
    const term = searchTerm.toLowerCase();
    return (
      fullName.includes(term) ||
      (inv.email ?? "").toLowerCase().includes(term) ||
      (inv.phoneNumber ?? "").toLowerCase().includes(term)
    );
  });

  const handleFormChange = (field: keyof InviteFormState, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<InviteFormState> = {};

    if (!form.firstName.trim()) {
      errors.firstName = t("validation.required");
    }
    if (!form.lastName.trim()) {
      errors.lastName = t("validation.required");
    }
    if (
      (form.notificationMethod === "EMAIL" ||
        form.notificationMethod === "BOTH") &&
      !form.email.trim()
    ) {
      errors.email = t("validation.required");
    }
    if (
      form.notificationMethod === "SMS" ||
      form.notificationMethod === "BOTH"
    ) {
      // MED-1543: this only ever checked the field was not empty, so
      // "+22178000012299" — two digits too long for Senegal — was accepted and
      // the SMS went nowhere.
      if (!form.phoneNumber.trim()) {
        errors.phoneNumber = t("validation.required");
      } else if (!isPhoneNumberValidForCountry(form.phoneNumber)) {
        errors.phoneNumber = t("validation.phoneInvalid");
      }
    }
    if (parseTrialDays(form.trialDays) === null) {
      errors.trialDays = t("admin.doctorInvitations.form.trialDaysInvalid");
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const processFile = useCallback(
    (file: File) => {
      setParseError(null);
      setParsedRows([]);
      const name = file.name.toLowerCase();
      if (name.endsWith(".csv")) {
        Papa.parse<Record<string, string>>(file, {
          header: true,
          skipEmptyLines: true,
          complete: result => {
            if (result.errors.length > 0) {
              setParseError(t("admin.doctorInvitations.bulkImport.parseError"));
              return;
            }
            setParsedRows(parseRawRows(result.data));
          },
        });
      } else if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
        const reader = new FileReader();
        reader.onload = e => {
          try {
            const wb = XLSX.read(e.target?.result, { type: "array" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json<Record<string, string>>(ws, {
              defval: "",
            });
            setParsedRows(parseRawRows(rows));
          } catch {
            setParseError(t("admin.doctorInvitations.bulkImport.parseError"));
          }
        };
        reader.readAsArrayBuffer(file);
      } else {
        setParseError(
          t("admin.doctorInvitations.bulkImport.unsupportedFormat")
        );
      }
    },
    [t]
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  // MED-1473: a single invalid row blocks the whole send.
  // MED-1473: and the header and the button count only the valid rows — they
  // both read this one partition rather than `parsedRows.length`, which claimed
  // "2 doctors ready" and "Send 2 invitations" for a file with one bad row.
  const invalidRows = parsedRows.filter(rowHasErrors);
  const validRows = parsedRows.filter(row => !rowHasErrors(row));
  const hasInvalidRow = invalidRows.length > 0;

  // MED-1695: the batch-wide trial length; null blocks the send like an invalid row does.
  const bulkTrialDaysValue = parseTrialDays(bulkTrialDays);

  const handleBulkSubmit = () => {
    if (parsedRows.length === 0 || hasInvalidRow || bulkTrialDaysValue === null)
      return;
    const payload: BulkDoctorInvitationRequest = {
      invitations: parsedRows.map(row => {
        const inv: DoctorInvitationRequest = {
          firstName: row.firstName,
          lastName: row.lastName,
          notificationMethod: row.notificationMethod,
        };
        if (row.email) inv.email = row.email;
        if (row.phoneNumber) inv.phoneNumber = row.phoneNumber;
        return inv;
      }),
      trialDays: bulkTrialDaysValue,
    };
    dispatch(bulkInviteDoctorsRequest(payload));
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const payload: DoctorInvitationRequest = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      notificationMethod: form.notificationMethod,
      // validateForm() has just refused anything parseTrialDays() returns null for.
      trialDays: parseTrialDays(form.trialDays) ?? DEFAULT_TRIAL_DAYS,
    };

    if (form.email.trim()) {
      payload.email = form.email.trim();
    }
    if (form.phoneNumber.trim()) {
      payload.phoneNumber = form.phoneNumber.trim();
    }

    dispatch(inviteDoctorRequest(payload));
  };

  const handleCloseModal = () => {
    setShowInviteModal(false);
    setForm(EMPTY_FORM);
    setFormErrors({});
  };

  const handleResend = (id: number) => {
    setResendingId(id);
    dispatch(resendInvitationRequest(id));
  };

  const handleCancelClick = (id: number) => {
    setCancelTargetId(id);
    setShowCancelConfirm(true);
  };

  const handleCancelConfirm = () => {
    if (cancelTargetId === null) return;
    setCancellingId(cancelTargetId);
    dispatch(cancelInvitationRequest(cancelTargetId));
    setShowCancelConfirm(false);
    setCancelTargetId(null);
  };

  const handleCancelModalClose = () => {
    setShowCancelConfirm(false);
    setCancelTargetId(null);
  };

  const handleViewJourney = (id: number) => {
    setJourneyEvents([]);
    setJourneyError(false);
    setJourneyInvitationId(id);
  };

  const handleCloseJourney = () => {
    setJourneyInvitationId(null);
    setJourneyEvents([]);
    setJourneyError(false);
  };

  const journeyInvitation =
    invitations.find(inv => inv.id === journeyInvitationId) ?? null;

  const journeyEntries = journeyInvitation
    ? buildJourneyEntries(journeyInvitation, journeyEvents)
    : [];

  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex justify-between items-center page-header">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t("admin.doctorInvitations.title")}
          </h2>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={() => setShowBulkModal(true)}
            className="flex items-center space-x-2"
          >
            <Users className="w-4 h-4" />
            <span>{t("admin.doctorInvitations.bulkInvite")}</span>
          </Button>
          <Button
            onClick={() => setShowInviteModal(true)}
            className="flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{t("admin.doctorInvitations.invite")}</span>
          </Button>
        </div>
      </div>

      {/* Search + status filter */}
      <div className="flex flex-col gap-3 page-section sm:flex-row sm:items-center">
        <div className="flex rounded-lg border border-neutral-200 overflow-hidden shrink-0">
          {(
            [
              ["PENDING", "pending"],
              ["ACCEPTED", "accepted"],
              ["EXPIRED", "expired"],
            ] as [InvitationStatus, string][]
          ).map(([status, key]) => {
            const isActive = statusTab === status;
            return (
              <button
                key={status}
                onClick={() => setStatusTab(status)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary-600 text-white"
                    : "bg-white text-neutral-600 hover:bg-neutral-50"
                }`}
              >
                {t(`admin.doctorInvitations.filter.${key}`)}
                <span
                  className={`rounded-full px-1.5 py-0.5 text-xs font-semibold ${
                    isActive
                      ? "bg-white/25 text-white"
                      : "bg-neutral-100 text-neutral-500"
                  }`}
                >
                  {countOf(status)}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex-1">
          <Input
            placeholder={t("common.search")}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            leftIcon={<Search className="w-4 h-4" />}
          />
        </div>
      </div>

      {/* Invitation list */}
      <div className="page-section">
        {loading ? (
          <div className="py-12 text-center">
            <p className="text-sm text-neutral-500">{t("common.loading")}</p>
          </div>
        ) : filteredInvitations.length === 0 ? (
          <div className="py-12 text-center">
            <h3 className="mt-2 text-sm font-medium text-neutral-900">
              {t("admin.doctorInvitations.noInvitations")}
            </h3>
            <p className="mt-1 text-sm text-neutral-500">
              {t("admin.doctorInvitations.noInvitationsDescription")}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("admin.doctorInvitations.table.name")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("admin.doctorInvitations.table.email")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("admin.doctorInvitations.table.phone")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("admin.doctorInvitations.table.status")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("admin.doctorInvitations.table.clickStatus")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("admin.doctorInvitations.table.invitedAt")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("admin.doctorInvitations.table.trialDays")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("admin.doctorInvitations.table.resendCount")}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("admin.doctorInvitations.table.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 bg-white">
                {filteredInvitations.map(inv => (
                  <tr key={inv.id} className="hover:bg-neutral-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center">
                          <span className="text-xs font-medium text-neutral-600">
                            {inv.firstName?.[0]?.toUpperCase() ?? "?"}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-neutral-900">
                          {[inv.firstName, inv.lastName]
                            .filter(Boolean)
                            .join(" ") || "—"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                      {inv.email ?? "—"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                      {inv.phoneNumber
                        ? formatPhoneNumberForDisplay(inv.phoneNumber)
                        : "—"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={STATUS_BADGE_CLASS[inv.status]}>
                        {t(`admin.doctorInvitations.status.${inv.status}`)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {inv.firstClickedAt ? (
                        <div>
                          <span className={CLICK_STATUS_BADGE_CLASS.clicked}>
                            {t("admin.doctorInvitations.clickStatus.clicked")}
                          </span>
                          <p className="mt-1 text-xs text-neutral-500">
                            {formatDateTime(inv.firstClickedAt)}
                          </p>
                        </div>
                      ) : (
                        <span className={CLICK_STATUS_BADGE_CLASS.notClicked}>
                          {t("admin.doctorInvitations.clickStatus.notClicked")}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                      {formatDateTime(inv.invitedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                      {inv.trialDays === undefined
                        ? "—"
                        : inv.trialDays === 0
                          ? t("admin.doctorInvitations.table.noTrial")
                          : t("admin.doctorInvitations.table.trialDaysValue", {
                              count: inv.trialDays,
                            })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                      {inv.resendCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end">
                        <ActionMenu
                          ariaLabel={t("admin.doctorInvitations.table.actions")}
                          items={[
                            {
                              label: t(
                                "admin.doctorInvitations.journey.viewLink"
                              ),
                              icon: <History className="w-4 h-4" />,
                              onClick: () => handleViewJourney(inv.id),
                            },
                            ...(inv.status !== "ACCEPTED"
                              ? [
                                  {
                                    label: t(
                                      "admin.doctorInvitations.table.cancel"
                                    ),
                                    onClick: () => handleCancelClick(inv.id),
                                    disabled: cancellingId === inv.id,
                                    loading: cancellingId === inv.id,
                                    danger: true,
                                  },
                                ]
                              : []),
                            {
                              label: t("admin.doctorInvitations.table.resend"),
                              onClick: () => handleResend(inv.id),
                              disabled:
                                inv.status === "ACCEPTED" ||
                                resendingId === inv.id,
                              loading: resendingId === inv.id,
                            },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showCancelConfirm}
        onClose={handleCancelModalClose}
        onConfirm={handleCancelConfirm}
        title={t("admin.doctorInvitations.cancelConfirm.title")}
        message={t("admin.doctorInvitations.cancelConfirm.message")}
        confirmLabel={t("admin.doctorInvitations.cancelConfirm.confirm")}
        variant="danger"
        isLoading={cancelLoading}
      />

      {/* Invite Doctor Modal */}
      {showInviteModal && (
        <Modal
          isOpen={showInviteModal}
          onClose={handleCloseModal}
          title={t("admin.doctorInvitations.invite")}
        >
          <div className="p-6 space-y-4">
            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("admin.doctorInvitations.form.firstName")}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <Input
                value={form.firstName}
                onChange={e => handleFormChange("firstName", e.target.value)}
                placeholder={t("admin.doctorInvitations.form.firstName")}
              />
              {formErrors.firstName && (
                <p className="mt-1 text-xs text-red-500">
                  {formErrors.firstName}
                </p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("admin.doctorInvitations.form.lastName")}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <Input
                value={form.lastName}
                onChange={e => handleFormChange("lastName", e.target.value)}
                placeholder={t("admin.doctorInvitations.form.lastName")}
              />
              {formErrors.lastName && (
                <p className="mt-1 text-xs text-red-500">
                  {formErrors.lastName}
                </p>
              )}
            </div>

            {/* Notification Method */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("admin.doctorInvitations.form.notificationMethod")}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <select
                className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                value={form.notificationMethod}
                onChange={e =>
                  handleFormChange(
                    "notificationMethod",
                    e.target.value as NotificationMethod
                  )
                }
              >
                {NOTIFICATION_METHODS.map(method => (
                  <option key={method} value={method}>
                    {method}
                  </option>
                ))}
              </select>
            </div>

            {/* Email */}
            {(form.notificationMethod === "EMAIL" ||
              form.notificationMethod === "BOTH") && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("admin.doctorInvitations.form.email")}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={e => handleFormChange("email", e.target.value)}
                  placeholder={t("admin.doctorInvitations.form.email")}
                />
                {formErrors.email && (
                  <p className="mt-1 text-xs text-red-500">
                    {formErrors.email}
                  </p>
                )}
              </div>
            )}

            {/* Phone Number */}
            {(form.notificationMethod === "SMS" ||
              form.notificationMethod === "BOTH") && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("admin.doctorInvitations.form.phoneNumber")}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <PhoneInput
                  value={form.phoneNumber}
                  onChange={e =>
                    handleFormChange("phoneNumber", e.target.value)
                  }
                  placeholder={t("admin.doctorInvitations.form.phoneNumber")}
                />
                {formErrors.phoneNumber && (
                  <p className="mt-1 text-xs text-red-500">
                    {formErrors.phoneNumber}
                  </p>
                )}
              </div>
            )}

            {/* Trial length (MED-1695) */}
            <div>
              <label
                htmlFor="invite-trial-days"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("admin.doctorInvitations.form.trialDays")}
              </label>
              <Input
                id="invite-trial-days"
                type="number"
                min={0}
                max={MAX_TRIAL_DAYS}
                step={1}
                value={form.trialDays}
                onChange={e => handleFormChange("trialDays", e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t("admin.doctorInvitations.form.trialDaysHint")}
              </p>
              {formErrors.trialDays && (
                <p className="mt-1 text-xs text-red-500">
                  {formErrors.trialDays}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end pt-2 space-x-3">
              <Button variant="outline" onClick={handleCloseModal}>
                {t("common.cancel")}
              </Button>
              <Button onClick={handleSubmit} disabled={inviteLoading}>
                {inviteLoading
                  ? t("common.processing")
                  : t("admin.doctorInvitations.form.submit")}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Bulk Import Modal */}
      {showBulkModal && (
        <Modal
          isOpen={showBulkModal}
          onClose={() => {
            setShowBulkModal(false);
            setParsedRows([]);
            setParseError(null);
          }}
          title={t("admin.doctorInvitations.bulkImport.title")}
        >
          <div className="p-6 space-y-5 max-h-[80vh] overflow-y-auto">
            {/* Instructions + template download */}
            <div className="flex items-start justify-between gap-4 rounded-lg bg-blue-50 border border-blue-100 p-4">
              <div className="text-sm text-blue-800 space-y-1">
                <p className="font-medium">
                  {t("admin.doctorInvitations.bulkImport.instructions")}
                </p>
                <p className="text-xs text-blue-600">
                  {t("admin.doctorInvitations.bulkImport.columns")}:{" "}
                  <code className="font-mono">
                    firstName, lastName, email, phoneNumber, notificationMethod
                    (EMAIL/SMS/BOTH)
                  </code>
                </p>
              </div>
              <button
                type="button"
                onClick={downloadTemplate}
                className="shrink-0 text-xs font-medium text-blue-700 underline hover:text-blue-900 whitespace-nowrap"
              >
                {t("admin.doctorInvitations.bulkImport.downloadTemplate")}
              </button>
            </div>

            {/* Drop zone */}
            {parsedRows.length === 0 && (
              <div
                onDragOver={e => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-10 cursor-pointer transition-colors ${
                  isDragging
                    ? "border-primary-500 bg-primary-50"
                    : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100"
                }`}
              >
                <Users className="w-8 h-8 text-gray-400" />
                <p className="text-sm font-medium text-gray-600">
                  {t("admin.doctorInvitations.bulkImport.dropZone")}
                </p>
                <p className="text-xs text-gray-400">
                  {t("admin.doctorInvitations.bulkImport.acceptedFormats")}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
            )}

            {/* Parse error */}
            {parseError && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                {parseError}
              </div>
            )}

            {/* Preview table */}
            {parsedRows.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-700">
                    {hasInvalidRow
                      ? t("admin.doctorInvitations.bulkImport.previewMixed", {
                          valid: validRows.length,
                          invalid: invalidRows.length,
                        })
                      : t("admin.doctorInvitations.bulkImport.preview", {
                          count: validRows.length,
                        })}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setParsedRows([]);
                      setParseError(null);
                    }}
                    className="text-xs text-gray-500 underline hover:text-gray-700"
                  >
                    {t("admin.doctorInvitations.bulkImport.changeFile")}
                  </button>
                </div>
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">
                          #
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">
                          {t("admin.doctorInvitations.form.firstName")}
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">
                          {t("admin.doctorInvitations.form.lastName")}
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">
                          {t("admin.doctorInvitations.form.email")}
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">
                          {t("admin.doctorInvitations.form.phoneNumber")}
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">
                          {t("admin.doctorInvitations.form.notificationMethod")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {parsedRows.map((row, i) => {
                        const hasError = rowHasErrors(row);
                        return (
                          <tr key={i} className={hasError ? "bg-red-50" : ""}>
                            <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                            <td
                              className={`px-3 py-2 ${!row.firstName ? "text-red-600 font-medium" : "text-gray-700"}`}
                            >
                              {row.firstName || "—"}
                            </td>
                            <td
                              className={`px-3 py-2 ${!row.lastName ? "text-red-600 font-medium" : "text-gray-700"}`}
                            >
                              {row.lastName || "—"}
                            </td>
                            <td className="px-3 py-2 text-gray-600">
                              {row.email || "—"}
                            </td>
                            <td className="px-3 py-2 text-gray-600">
                              {row.phoneNumber
                                ? formatPhoneNumberForDisplay(row.phoneNumber)
                                : "—"}
                            </td>
                            <td className="px-3 py-2 text-gray-600">
                              {notificationMethodLabel(
                                t,
                                row.notificationMethod
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {hasInvalidRow && (
                  <p className="text-xs text-red-600">
                    {t("admin.doctorInvitations.bulkImport.rowErrors")}
                  </p>
                )}
              </div>
            )}

            {/* Bulk validation errors — per-row duplicates returned by the API */}
            {invitationsError &&
              invitationsError.code ===
                "DOCTOR_INVITATION_BULK_VALIDATION_FAILED" &&
              Array.isArray(invitationsError.data) && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm font-medium text-red-700 mb-2">
                    {t(
                      "admin.doctorInvitations.bulkImport.bulkValidationFailed"
                    )}
                  </p>
                  <ul className="text-sm text-red-600 space-y-1">
                    {invitationsError.data.map((e: BulkRowError, i: number) => (
                      <li key={i}>
                        {e.errorCode ===
                        "DOCTOR_INVITATION_EMAIL_ALREADY_EXISTS"
                          ? t(
                              "admin.doctorInvitations.bulkImport.emailDuplicate",
                              { row: e.row, value: e.email ?? "" }
                            )
                          : t(
                              "admin.doctorInvitations.bulkImport.phoneDuplicate",
                              { row: e.row, value: e.phoneNumber ?? "" }
                            )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            {/* Batch trial length (MED-1695): one value for every row of the file */}
            <div>
              <label
                htmlFor="bulk-trial-days"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("admin.doctorInvitations.form.trialDays")}
              </label>
              <Input
                id="bulk-trial-days"
                type="number"
                min={0}
                max={MAX_TRIAL_DAYS}
                step={1}
                value={bulkTrialDays}
                onChange={e => setBulkTrialDays(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t("admin.doctorInvitations.form.trialDaysHint")}
              </p>
              {bulkTrialDaysValue === null && (
                <p className="mt-1 text-xs text-red-500">
                  {t("admin.doctorInvitations.form.trialDaysInvalid")}
                </p>
              )}
            </div>

            <div className="flex justify-end pt-2 space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowBulkModal(false);
                  setParsedRows([]);
                  setParseError(null);
                }}
              >
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleBulkSubmit}
                loading={bulkInviteLoading}
                disabled={
                  bulkInviteLoading ||
                  parsedRows.length === 0 ||
                  hasInvalidRow ||
                  bulkTrialDaysValue === null
                }
              >
                {t("admin.doctorInvitations.bulkImport.submit", {
                  count: validRows.length,
                })}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Invitation Journey Modal */}
      {journeyInvitationId !== null && (
        <Modal
          isOpen={journeyInvitationId !== null}
          onClose={handleCloseJourney}
          title={t("admin.doctorInvitations.journey.title")}
        >
          <div className="p-6 space-y-4">
            {journeyLoading ? (
              <p className="text-sm text-neutral-500">
                {t("admin.doctorInvitations.journey.loading")}
              </p>
            ) : journeyError ? (
              <p className="text-sm text-red-600">
                {t("admin.doctorInvitations.journey.error")}
              </p>
            ) : (
              <ol className="space-y-4">
                {journeyEntries.map((entry, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-2.5 h-2.5 mt-1.5 rounded-full bg-primary-500" />
                    <div>
                      <p className="text-sm font-medium text-neutral-900">
                        {entry.type === "CLICKED"
                          ? t(
                              `admin.doctorInvitations.journey.events.${
                                entry.outcome === "EXPIRED"
                                  ? "linkClickedExpired"
                                  : entry.outcome === "ALREADY_ACCEPTED"
                                    ? "linkClickedAlreadyAccepted"
                                    : "linkClicked"
                              }`
                            )
                          : t(
                              `admin.doctorInvitations.journey.events.${entry.type.toLowerCase()}`
                            )}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {formatTimestamp(entry.timestamp)}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            )}

            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={handleCloseJourney}>
                {t("common.close")}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
